import React, { useReducer, useRef, useEffect } from 'react'
import PolaroidCarouselPricingCard from '../../components/polaroidCarouselPricing/polaroidCarouselPricingCard'
import leftArrow from '../../assets/left-arrow.svg'
import rightArrow from '../../assets/right-arrow.svg'
import './polaroidcarousel.css'
import { isMobile } from 'react-device-detect'
import { isEmpty } from 'lodash'

const PolaroidCarouselPricing = ({ data }) => {
  const reducer = (state, action) => {
    switch (action.type) {
      case 'NEXT':
        return {
          ...state,
          index: (state.index + 1) % data?.subscription_plan.length,
          direction: 'right',
          animating: true,
        }
      case 'PREV':
        return {
          ...state,
          index:
            (state.index - 1 + data?.subscription_plan.length) % data?.subscription_plan.length,
          direction: 'left',
          animating: true,
        }
      case 'ANIMATION_DONE':
        return {
          ...state,
          animating: false,
        }
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    index: 1,
    direction: null,
    animating: false,
  })
  const carouselRef = useRef(null)
  const touchStartX = useRef(0)
  const touchEndX = useRef(0)
  const animationTimeoutRef = useRef(null)

  useEffect(() => {
    // Clean up any animation timeouts when component unmounts
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (state.animating) {
      // Reset animation state after animation completes
      animationTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'ANIMATION_DONE' })
      }, 500) // Match this to your animation duration (500ms)
    }
  }, [state.animating])

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    const minSwipeDistance = 50
    const distance = touchStartX.current - touchEndX.current

    if (Math.abs(distance) >= minSwipeDistance && !state.animating) {
      if (distance > 0) {
        // Swiped left, go to next
        dispatch({ type: 'NEXT' })
      } else {
        // Swiped right, go to previous
        dispatch({ type: 'PREV' })
      }
    }
  }

  const getIndices = () => {
    if (isEmpty(data?.subscription_plan)) return
    const left = (state.index - 1 + data?.subscription_plan.length) % data?.subscription_plan.length
    const right = (state.index + 1) % data?.subscription_plan.length

    const isMobileView = window.matchMedia('(max-width: 500px)').matches

    if (isMobileView) {
      return [state.index]
    }

    return [left, state.index, right]
  }

  const getAnimationClass = (idx) => {
    // Only animate the center card (or the single card on mobile)
    if ((getIndices().length === 1 || idx === 1) && state.direction) {
      return state.direction === 'right' ? 'slide-in-left' : 'slide-in-right'
    }
    return ''
  }

  const getStyleValue = (parentKey, childKey, defaultValue) => {
    if (!data?.style_config) {
      return defaultValue ? defaultValue : ''
    }

    let tempArr = data?.style_config[parentKey]?.data.filter((info) => info.key === childKey)

    return !isEmpty(tempArr) ? (tempArr[0].value ? tempArr[0].value : defaultValue) : defaultValue
  }

  return (
    <div
      className="w-full h-full text-center py-5 px-4 md:px-6 lg:px-8 xl:py-10"
      style={{ background: getStyleValue('component_background', 'primary_color', '') }}
    >
      <div
        className={`${getStyleValue(
          'main_heading',
          'font_format',
          'font-bold',
        )} text-xl md:text-2xl xl:text-3xl`}
        style={{
          fontFamily: getStyleValue('main_heading', 'font', ''),
          color: getStyleValue('main_heading', 'color', '#e2e2e2'),
        }}
      >
        {data?.main_heading}
      </div>

      <div className="relative mt-8 md:mt-12 lg:mt-16 flex items-center justify-center gap-4 w-full">
        {/* Left Arrow */}
        <div
          onClick={() => !state.animating && dispatch({ type: 'PREV' })}
          className={`cursor-pointer w-8 h-8 bg-no-repeat bg-center bg-contain z-10 ${
            state.animating ? 'opacity-50' : ''
          }`}
          style={{ backgroundImage: `url(${leftArrow})` }}
        />

        <div
          ref={carouselRef}
          className="flex gap-2 md:gap-4 lg:gap-6 overflow-hidden justify-center"
          onTouchStart={isMobile ? handleTouchStart : undefined}
          onTouchMove={isMobile ? handleTouchMove : undefined}
          onTouchEnd={isMobile ? handleTouchEnd : undefined}
        >
          {getIndices().map((index, idx) => (
            <div
              key={`${index}-${idx}`}
              className={`${getIndices().length === 1 || idx === 1 ? '' : 'scale-90 opacity-40'}`}
              style={{
                minWidth: '150px',
                transition: 'transform 0.3s, opacity 0.3s',
              }}
            >
              <PolaroidCarouselPricingCard
                item={data?.subscription_plan[index]}
                animationClass={getAnimationClass(idx)}
              />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        <div
          onClick={() => !state.animating && dispatch({ type: 'NEXT' })}
          className={`cursor-pointer w-8 h-8 bg-no-repeat bg-center bg-contain z-10 ${
            state.animating ? 'opacity-50' : ''
          }`}
          style={{ backgroundImage: `url(${rightArrow})` }}
        />
      </div>
    </div>
  )
}

export default PolaroidCarouselPricing
